import * as React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Search,
  Plus,
  Users,
  Loader2,
  Trash2,
  Pencil,
  Eye,
  Filter,
  UserPlus,
  Lock,
  MoreVertical,
  XCircle,
} from 'lucide-react'
import {
  useWAAudiences,
  useUpdateWAAudience,
  useDeleteWAAudience,
  usePreviewWAAudience,
} from '@/hooks/useWAAudiences'
import { AudienceSelector } from './audience-selector'
import { ContactPicker } from './contact-picker'
import type {
  WAAudience,
  WAAudienceType,
  WAAudiencesListParams,
  WAAudienceFilter,
  WAAudienceContactSearchResult,
} from '@/lib/api-client'

const TYPE_BADGES: Record<WAAudienceType, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  dynamic: { label: 'Dynamic', variant: 'default' },
  static: { label: 'Static', variant: 'secondary' },
  manual: { label: 'Manual', variant: 'outline' },
}

export function AudienceList() {
  const navigate = useNavigate()

  // State
  const [searchQuery, setSearchQuery] = React.useState('')
  const [typeFilter, setTypeFilter] = React.useState<WAAudienceType | 'all'>('all')
  const [page, setPage] = React.useState(1)

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = React.useState(false)
  const [editingAudience, setEditingAudience] = React.useState<WAAudience | null>(null)

  // Delete dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false)
  const [targetAudience, setTargetAudience] = React.useState<WAAudience | null>(null)

  // Preview dialog
  const [previewOpen, setPreviewOpen] = React.useState(false)
  const [previewAudience, setPreviewAudience] = React.useState<WAAudience | null>(null)

  // Queries
  const params: WAAudiencesListParams = {
    page,
    limit: 20,
    search: searchQuery || undefined,
    type: typeFilter !== 'all' ? typeFilter : undefined,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  }
  const { data, isPending, isError, refetch } = useWAAudiences(params)

  // Mutations
  const deleteMutation = useDeleteWAAudience()
  const previewMutation = usePreviewWAAudience()

  const audiences = data?.data || []
  const pagination = data?.pagination

  // Handlers
  const handleCreate = () => {
    navigate({ to: '/whatsapp-audiences/new' })
  }

  const handleEdit = (audience: WAAudience) => {
    setEditingAudience(audience)
    setEditDialogOpen(true)
  }

  const handleDelete = (audience: WAAudience) => {
    setTargetAudience(audience)
    setDeleteConfirmOpen(true)
  }

  const confirmDelete = () => {
    if (targetAudience) {
      deleteMutation.mutate(targetAudience.id, {
        onSuccess: () => setDeleteConfirmOpen(false),
      })
    }
  }

  const handlePreview = (audience: WAAudience) => {
    setPreviewAudience(audience)
    setPreviewOpen(true)
    previewMutation.mutate(audience.id)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setPage(1)
  }

  const handleTypeChange = (value: string) => {
    setTypeFilter(value as WAAudienceType | 'all')
    setPage(1)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar - consistent with Templates/Campaigns pages */}
      <div className="flex-shrink-0 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 border-b border-border bg-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="relative w-full sm:w-auto sm:min-w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <Input
              type="text"
              placeholder="Search audiences..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-9 h-10 text-sm bg-card border border-border rounded-lg"
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Select value={typeFilter} onValueChange={handleTypeChange}>
              <SelectTrigger className="h-10 text-sm bg-card border border-border min-w-[130px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="dynamic">Dynamic</SelectItem>
                <SelectItem value="static">Static</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
              </SelectContent>
            </Select>

            <Button
              onClick={handleCreate}
              variant="default"
              size="default"
              className="h-10"
            >
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Create Audience</span>
              <span className="sm:hidden">Create</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-3 sm:p-4 lg:p-6">
        {isPending ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <AudienceCardSkeleton key={i} />
            ))}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-12">
            <XCircle className="w-12 h-12 text-destructive mb-4" />
            <p className="text-sm text-text-muted mb-4">Failed to load audiences</p>
            <Button variant="outline" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : audiences.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Users className="w-12 h-12 text-text-muted mb-4" />
            <p className="text-sm text-text-muted mb-2">No audiences found</p>
            <p className="text-xs text-text-muted mb-4">
              {searchQuery || typeFilter !== 'all'
                ? 'Try changing filters or search'
                : 'Create reusable audience segments for your campaigns'}
            </p>
            {!searchQuery && typeFilter === 'all' && (
              <Button onClick={handleCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Create Audience
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {audiences.map((audience) => (
                <AudienceCard
                  key={audience.id}
                  audience={audience}
                  onEdit={() => handleEdit(audience)}
                  onDelete={() => handleDelete(audience)}
                  onPreview={() => handlePreview(audience)}
                />
              ))}
            </div>

            {/* Pagination - consistent with Templates/Campaigns */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-text-muted px-4">
                  Page {page} / {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={page >= pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Dialog */}
      <AudienceEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        audience={editingAudience}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Audience?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{targetAudience?.name}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Audience Preview: {previewAudience?.name}</DialogTitle>
            <DialogDescription>
              {previewAudience?.type === 'dynamic'
                ? 'Live preview — count may change'
                : `${previewAudience?.type === 'static' ? 'Frozen' : 'Manual'} contact list`}
            </DialogDescription>
          </DialogHeader>
          {previewMutation.isPending ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
            </div>
          ) : previewMutation.data ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
                <Users className="w-5 h-5 text-primary" />
                <span className="text-lg font-semibold">{previewMutation.data.totalCount.toLocaleString()}</span>
                <span className="text-sm text-text-muted">contacts</span>
              </div>
              {previewMutation.data.sampleContacts.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs text-text-muted">Sample contacts:</p>
                  {previewMutation.data.sampleContacts.map((contact) => (
                    <div
                      key={contact.contactId}
                      className="flex items-center justify-between p-2 rounded-md bg-muted/30 text-xs"
                    >
                      <div>
                        <span className="font-medium">{contact.patientName}</span>
                        <span className="text-text-muted ml-2">({contact.patientPno})</span>
                      </div>
                      <Badge variant="outline" className="text-[10px]">
                        {contact.branchName}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ============================================================================
// Audience Card - consistent with TemplateCard / CampaignCard
// ============================================================================

function AudienceCard({
  audience,
  onEdit,
  onDelete,
  onPreview,
}: {
  audience: WAAudience
  onEdit: () => void
  onDelete: () => void
  onPreview: () => void
}) {
  const typeBadge = TYPE_BADGES[audience.type]
  const TypeIcon = audience.type === 'dynamic' ? Filter
    : audience.type === 'static' ? Lock
    : UserPlus

  return (
    <div className="flex flex-col p-4 rounded-lg border border-border bg-card hover:bg-hover transition-colors">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
            <TypeIcon className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <h3 className="font-medium text-text-dark truncate">{audience.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={typeBadge.variant} className="text-[10px] px-1.5 py-0 h-5">
                {typeBadge.label}
              </Badge>
              {audience.description && (
                <span className="text-[11px] text-text-muted truncate max-w-[120px]" title={audience.description}>
                  {audience.description}
                </span>
              )}
            </div>
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem onClick={onPreview}>
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onEdit}>
              <Pencil className="w-4 h-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/30 mb-3">
        <Users className="w-3.5 h-3.5 text-text-muted" />
        <div className="text-xs">
          <span className="font-medium text-text-dark">
            {(audience.estimatedCount || 0).toLocaleString()}
          </span>
          <span className="text-text-muted ml-1">contacts</span>
        </div>
      </div>

      {/* Filter summary for dynamic */}
      {audience.type === 'dynamic' && audience.filters && (
        <div className="flex-1 mb-3">
          <div className="flex flex-wrap gap-1">
            {audience.filters.branchIds && audience.filters.branchIds.length > 0 && (
              <Badge variant="outline" className="text-[10px]">
                {audience.filters.branchIds.length} branch{audience.filters.branchIds.length > 1 ? 'es' : ''}
              </Badge>
            )}
            {audience.filters.inactiveDays && (
              <Badge variant="outline" className="text-[10px]">
                {audience.filters.inactiveDays}+ days inactive
              </Badge>
            )}
            {audience.filters.gender && (
              <Badge variant="outline" className="text-[10px]">
                {audience.filters.gender}
              </Badge>
            )}
            {audience.filters.minTotalSpent && (
              <Badge variant="outline" className="text-[10px]">
                RM{audience.filters.minTotalSpent}+
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-border/50 mt-auto">
        <span className="text-[10px] text-text-muted">
          {new Date(audience.createdAt).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </span>
        {audience.lastEvaluatedAt && (
          <span className="text-[10px] text-text-muted">
            Updated {new Date(audience.lastEvaluatedAt).toLocaleDateString('en-MY')}
          </span>
        )}
      </div>
    </div>
  )
}

// Skeleton - consistent with CampaignCardSkeleton / TemplateCardSkeleton
function AudienceCardSkeleton() {
  return (
    <div className="flex flex-col p-4 rounded-lg border border-border bg-card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 flex-1">
          <Skeleton className="h-8 w-8 rounded-md" />
          <div className="flex-1">
            <Skeleton className="h-4 w-32 mb-2" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-4 w-20" />
            </div>
          </div>
        </div>
        <Skeleton className="h-8 w-8 rounded" />
      </div>
      <Skeleton className="h-8 w-full rounded-md mb-3" />
      <div className="flex items-center justify-between pt-2 border-t border-border/50">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
  )
}

// ============================================================================
// Edit Dialog (edit only — create uses /whatsapp-audiences/new page)
// ============================================================================

function AudienceEditDialog({
  open,
  onOpenChange,
  audience,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  audience: WAAudience | null
}) {
  // Form state
  const [name, setName] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [filters, setFilters] = React.useState<WAAudienceFilter>({})
  const [selectedContacts, setSelectedContacts] = React.useState<WAAudienceContactSearchResult[]>([])

  const updateMutation = useUpdateWAAudience()

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open && audience) {
      setName(audience.name)
      setDescription(audience.description || '')
      setFilters(audience.filters || {})
      setSelectedContacts([])
    }
  }, [open, audience])

  if (!audience) return null

  const handleSubmit = () => {
    if (!name.trim()) return

    updateMutation.mutate({
      id: audience.id,
      data: {
        name: name.trim(),
        description: description.trim() || undefined,
        filters: audience.type === 'dynamic' ? filters : undefined,
        contactIds: audience.type === 'manual' ? selectedContacts.map(c => c.contactId) : undefined,
      },
    }, {
      onSuccess: () => onOpenChange(false),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Audience</DialogTitle>
          <DialogDescription>
            Update audience settings and filters
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Name & Description */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label htmlFor="aud-name" className="text-sm font-medium mb-2 block">
                Name
              </Label>
              <Input
                id="aud-name"
                placeholder="e.g. VIP Patients, Inactive 30d+"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-10 bg-card border border-border rounded-lg"
              />
            </div>
            <div>
              <Label htmlFor="aud-desc" className="text-sm font-medium mb-2 block">
                Description (optional)
              </Label>
              <Textarea
                id="aud-desc"
                placeholder="Brief notes about this audience..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="resize-none"
              />
            </div>
          </div>

          {/* Dynamic: filter builder */}
          {audience.type === 'dynamic' && (
            <div className="bg-muted/20 rounded-lg p-4 border border-border/50">
              <AudienceSelector
                value={filters}
                onChange={setFilters}
              />
            </div>
          )}

          {/* Manual: contact picker */}
          {audience.type === 'manual' && (
            <div className="bg-muted/20 rounded-lg p-4 border border-border/50">
              <Label className="text-sm font-medium mb-3 block">Select Contacts</Label>
              <ContactPicker
                selectedContacts={selectedContacts}
                onContactsChange={setSelectedContacts}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || updateMutation.isPending}>
            {updateMutation.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
